import { createSelector } from '@reduxjs/toolkit';

/**
 * Returns the complete catalog state
 *
 * @param {object} state - Current state
 * @returns {object} - The entire catalog state
 */
export const getCatalogState = (state) => state.catalog;

/**
 * Returns the service names
 *
 * @param {object} state - Current state
 * @returns {array} - A list of valid service names from the catalog
 */
export const getCatalogServicesNames = (state) =>
  getCatalogState(state)
    .servicesCatalog.services.filter((service) => service.displaySetting === 1)
    .map((item) => item.name);

/**
 * Returns the regions catalog state
 *
 * @param {object} state - Current state
 * @returns {object} - The regions catalog state
 */
export const getRegionsState = (state) => getCatalogState(state).regionsCatalog;

export const getConfigurationState = (state) => getCatalogState(state).configurationsCatalog;

export const getRegionsByCloudProvider = (state) => (cloudProviderId) => {
  const { regions } = getRegionsState(state);
  return regions.filter(
    (region) => region.cloudProvider === cloudProviderId && region.displaySetting === 1,
  );
};

export const getValidFlavorsByServiceSubtypeId = (state) => (serviceSubtypeId) => {
  const {
    flavorsCatalog: { flavors },
  } = getCatalogState(state);
  const validFlavors = flavors.filter(
    (flavor) => flavor.serviceSubtype === serviceSubtypeId && flavor.displaySetting === 1,
  );
  return validFlavors;
};

export const getServiceSubtypeIdByName = (state) => (serviceSubtypeName) => {
  const {
    serviceSubtypesCatalog: { serviceSubtypes },
  } = getCatalogState(state);
  return serviceSubtypes.filter((serviceSubtype) => serviceSubtype.name === serviceSubtypeName)[0]
    .id;
};

export const getValidFlavorsByServiceSubtypeName = (state) => (serviceSubtypeName) => {
  const serviceSubtypeId = getServiceSubtypeIdByName(state)(serviceSubtypeName);
  const validFlavors = getValidFlavorsByServiceSubtypeId(state)(serviceSubtypeId);
  return validFlavors;
};

export const createInstanceStateSelector = (state) => state.createInstance;

export const regionsByServiceSubtypeSelector = createSelector(
  getRegionsState,
  createInstanceStateSelector,
  (regionsState, createInstanceState) => {
    const { regions } = regionsState;
    const { id: serviceSubtypeId } = createInstanceState.newInstance.newInstanceServiceSubtype;
    const { id: cloudProviderId } = createInstanceState.newInstance.newInstanceCloudProvider;
    const regionsByServiceSubtype = regions.filter(
      (region) =>
        region.serviceSubtypes.includes(serviceSubtypeId) &&
        region.cloudProvider === cloudProviderId,
    );
    return regionsByServiceSubtype;
  },
);

/**
 * Returns the cloud providers
 *
 * @param {object} state - Current state
 * @returns {array} - A list of cloud providers from the catalog
 */
export const cloudProvidersStateSelector = createSelector(
  getCatalogState,
  (state) => state.providersCatalog.providers,
);

/**
 * Returns the list of cloud provider names that are available for display
 *
 * @param {object} state - Current state
 * @returns {array} - A list of cloud provider names from the catalog
 */
export const cloudProvidersNamesSelector = createSelector(
  cloudProvidersStateSelector,
  (cloudProviders) =>
    cloudProviders
      .filter((cloudProvider) => cloudProvider.displaySetting === 1)
      .map((cloudProvider) => cloudProvider.name),
);

/**
 * Returns whether or not any part of the catalog is still loading
 *
 * @param {object} state - Current state
 * @returns {boolean} - Whether or not the catalog is still in a loading state
 */
export const catalogIsLoadingSelector = createSelector(
  getCatalogState,
  (catalog) =>
    catalog.servicesCatalog.isLoading ||
    catalog.serviceSubtypesCatalog.isLoading ||
    catalog.providersCatalog.isLoading ||
    catalog.versionsCatalog.isLoading ||
    catalog.regionsCatalog.isLoading ||
    catalog.plansCatalog.isLoading ||
    catalog.flavorsCatalog.isLoading ||
    catalog.addonsCatalog.isLoading ||
    catalog.configurationsCatalog.isLoading,
);

export default getCatalogState;
